// Real .xlsx export for RACI, timetable and priority matrix, styled in VERA's colours.

import type { Artifact } from "./artifacts";
import { desktop } from "./desktop";

const VIOLET = "FF6F3FD1";
const HEAD = "FF1D1538";
const RACI_FILL: Record<string, string> = { R: "FFD9C7FF", A: "FFFFD3DF", C: "FFCFF3E2", I: "FFEEEEF3" };

export async function toWorkbook(a: Artifact): Promise<Uint8Array> {
  const { default: ExcelJS } = await import("exceljs");
  const wb = new ExcelJS.Workbook();
  wb.creator = "VERA Studio";
  wb.created = new Date();

  const header = (ws: import("exceljs").Worksheet, row: number) => {
    ws.getRow(row).eachCell((c) => {
      c.font = { bold: true, color: { argb: "FFFFFFFF" } };
      c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEAD } };
      c.alignment = { vertical: "middle", horizontal: "center" };
    });
  };
  const title = (ws: import("exceljs").Worksheet, text: string, span: number) => {
    ws.mergeCells(1, 1, 1, span);
    const c = ws.getCell(1, 1);
    c.value = text;
    c.font = { bold: true, size: 14, color: { argb: VIOLET } };
    ws.getRow(1).height = 24;
  };

  if (a.kind === "raci") {
    const ws = wb.addWorksheet("RACI", { views: [{ state: "frozen", xSplit: 1, ySplit: 3 }] });
    title(ws, a.title, a.people.length + 1);
    ws.getRow(3).values = ["Task", ...a.people];
    header(ws, 3);
    a.rows.forEach((r, i) => {
      const row = ws.getRow(4 + i);
      row.values = [r.task, ...r.cells];
      r.cells.forEach((v, j) => {
        const c = row.getCell(j + 2);
        c.alignment = { horizontal: "center" };
        if (v) { c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: RACI_FILL[v] } }; c.font = { bold: true }; }
      });
    });
    ws.getColumn(1).width = 34;
    a.people.forEach((_, j) => (ws.getColumn(j + 2).width = 11));
    const legend = 5 + a.rows.length;
    ws.getCell(legend, 1).value = "R = does the work · A = owns the outcome (one per task) · C = gives input · I = kept informed";
    ws.getCell(legend, 1).font = { italic: true, color: { argb: "FF6B6B80" } };
  } else if (a.kind === "timetable") {
    const ws = wb.addWorksheet("Timetable", { views: [{ state: "frozen", xSplit: 2, ySplit: 3 }] });
    title(ws, a.title, a.weeks + 2);
    ws.getRow(3).values = ["Task", "Owner", ...Array.from({ length: a.weeks }, (_, i) => `Week ${i + 1}`)];
    header(ws, 3);
    a.rows.forEach((r, i) => {
      const row = ws.getRow(4 + i);
      row.values = [r.task, r.owner];
      for (let w = r.start; w <= r.end; w++) {
        const c = row.getCell(2 + w);
        c.value = r.milestone ? "◆" : "";
        c.alignment = { horizontal: "center" };
        c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: r.milestone ? "FFFFB3C7" : "FFB896FF" } };
      }
    });
    ws.getColumn(1).width = 30;
    ws.getColumn(2).width = 12;
    for (let w = 0; w < a.weeks; w++) ws.getColumn(3 + w).width = 10;
  } else if (a.kind === "matrix") {
    const ws = wb.addWorksheet("Priorities");
    title(ws, a.title, 2);
    const [q1, q2, q3, q4] = a.quadrants;
    const block = (r: number, left: typeof q1, right: typeof q1) => {
      ws.getRow(r).values = [left.name, right.name];
      header(ws, r);
      const n = Math.max(left.items.length, right.items.length);
      for (let i = 0; i < n; i++) ws.getRow(r + 1 + i).values = [left.items[i] ?? "", right.items[i] ?? ""];
      return r + 2 + n;
    };
    const next = block(3, q1, q2);
    block(next, q3, q4);
    ws.getColumn(1).width = 40;
    ws.getColumn(2).width = 40;
  } else {
    const ws = wb.addWorksheet("Document");
    ws.getCell("A1").value = a.title;
    ws.getCell("A3").value = a.body;
  }

  const buf = await wb.xlsx.writeBuffer();
  return new Uint8Array(buf as ArrayBuffer);
}

/** Saves bytes: on the desktop through a Save dialog, in the browser as a download. Returns where it went. */
/** Opens a file straight away: in its usual app on VERA for desktop, in a new tab in the browser. */
export async function openFile(name: string, bytes: Uint8Array, mime: string): Promise<void> {
  if (desktop?.files.open) { await desktop.files.open(name, bytes); return; }
  if (desktop) { await desktop.files.saveAs(name, bytes); return; } // an older desktop build: save instead
  // A browser tab can't show Word or Excel: it would save the file without its name or extension (which then opens as
  // text in Notepad). So on the web, Word and Excel download with their real name; only a PDF opens in a tab.
  if (mime !== "application/pdf") { await saveFile(name, bytes, mime); return; }
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: mime }));
  if (!window.open(url, "_blank")) { await saveFile(name, bytes, mime); }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function saveFile(name: string, bytes: Uint8Array, mime: string): Promise<string | null> {
  if (desktop) return desktop.files.saveAs(name, bytes);
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: mime }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
  return name;
}

export const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
